import React from 'react';
import { useSelector } from 'react-redux';
import { filter, find } from 'lodash';
import PropTypes, { any } from 'prop-types';

import { required, createReactFormValidator, range, positiveInteger } from '../../../../../utils/validation';
import { acceptanceValues, acceptanceRankingValues, divisionGenderTypes, acceptanceBucketTypes, acceptanceCriteriaList, sportTypeIds, utrTypeIds, defaultMaxPlayersValue, teamPaymentTypeIds, verifiedAcceptanceMinValue, nonVerifiedAcceptanceMinValue, defaultAcceptanceMinValue } from '../../../../../utils/eventConstants';
import { isSinglesFormat, isDinglesOrDoublesFormat } from '../../../../../utils/events';
import {
    Select, ToggleSwitch, DefaultSelect, Input,
} from '../../../../Form';
import Buckets from './Buckets';
import AcceptanceRankTypeSelect from './AcceptanceRankTypeSelect';
import RankingBuckets from './RankingBuckets';

const _ = { filter, find };

const DivisionAcceptanceList = (props) => {
    const {
        field,
        formApi,
        division,
        divisionIndex,
        sportTypeId,
    } = props;

    const coreUI = useSelector(({ coreUI }) => coreUI);

    const { genderTypes } = coreUI.types.tms;
    const { formatTypes } = coreUI.types.tms.division;
    const { acceptanceListRatingTypes, acceptanceListRoundTypes } = coreUI.types.tms.division.tournament;
    const { isPTTEvent, $verified } = formApi.values;
    const mainRound = _.find(acceptanceListRoundTypes, { value: acceptanceBucketTypes.MAIN });
    const qualifyingRound = _.find(acceptanceListRoundTypes, { value: acceptanceBucketTypes.QUALIFYING });
    const isDivisionGenderMaleOrFemale = _.find(genderTypes, { value: divisionGenderTypes.ALL }).id !== parseInt(division.gender);
    const isDivisionGenderFemale = _.find(genderTypes, { value: divisionGenderTypes.FEMALE }).id === parseInt(division.gender);
    const isVerifiedEvent = parseInt($verified) === utrTypeIds.SANCTIONED;
    const requiredErrorMessage = 'This field is required';

    const getRatingType = () => {
        const formatType = formatTypes.find(f => f.id === parseInt(division.divisionFormatType));
        if (formatType?.value === 'singles') {
            return isVerifiedEvent ? 'VERIFIED_SINGLES' : 'SINGLES';
        }

        return isVerifiedEvent ? 'VERIFIED_DOUBLES' : 'DOUBLES';
    };

    const onAcceptanceTypeChange = (val) => {
        if (val !== acceptanceValues.WAIT_LIST && division?.allowTeamOnWaitlist) {
            formApi.setValue([field, divisionIndex, 'allowTeamOnWaitlist'], false);
        }
        if (val === acceptanceValues.CAPPED_REGISTRATION && !division?.maxPlayers) {
            formApi.setValue([field, divisionIndex, 'maxPlayers'], defaultMaxPlayersValue);
        }
        const hasMaxPlayersError = formApi.getError([field, divisionIndex, 'maxPlayers']);
        if (val === acceptanceValues.WAIT_LIST && hasMaxPlayersError === requiredErrorMessage) {
            formApi.setError([field, divisionIndex, 'maxPlayers'], null);
        }
        if (val === acceptanceValues.LIST) {
            if (hasMaxPlayersError) {
                formApi.setError([field, divisionIndex, 'maxPlayers'], null);
            }
            if (!division.acceptance || !division.acceptance.list || !division.acceptance.list.buckets) {
                formApi.setValue([field, divisionIndex, 'acceptance', 'list'], {
                    buckets: [
                        {
                            type: mainRound.id,
                            order: 1,
                            count: null,
                        },
                        {
                            type: qualifyingRound.id,
                            order: 2,
                            count: null,
                        },
                    ],
                    type: acceptanceRankingValues.UTR,
                    rating: _.find(acceptanceListRatingTypes, { value: getRatingType() }).id,
                });
            }
        }
    };

    const onAcceptanceRankTypeChange = (e) => {
        const val = e.target.value;
        if (val === acceptanceRankingValues.UTR) {
            if (!division.acceptance.list.rating) {
                formApi.setValue([field, divisionIndex, 'acceptance', 'list', 'rating'], _.find(acceptanceListRatingTypes, { value: 'VERIFIED_SINGLES' }).id);
            }
        }
    };

    const showUtrRatingInputs = division.acceptance?.type === acceptanceValues.LIST &&
        division.acceptance?.list?.type === acceptanceRankingValues.UTR;
    const showWtaRankingInputs = isDivisionGenderFemale && division.acceptance?.type === acceptanceValues.LIST &&
        division.acceptance?.list?.type === acceptanceRankingValues.WTA;
    const isDivisionSingles = isSinglesFormat(parseInt(division?.divisionFormatType));
    const isDivisionDoubleOrDingles = isDinglesOrDoublesFormat(parseInt(division?.divisionFormatType));

    const getDivisionSelectLabel = (isDivisionSingles, division) => {
        const label = isDivisionSingles ? 'players' : 'teams';
        if (division?.acceptance?.type === acceptanceValues.WAIT_LIST) {
            return `How many ${label} would you like to accept before the waitlist?`;
        }
        if (division?.acceptance?.type === acceptanceValues.CAPPED_REGISTRATION) {
            return `How many ${label} would you like to accept?`;
        }
    };

    const getDivisionAcceptanceList = (sportTypeId, acceptanceCriteriaList, isDivisionDoubleOrDingles) => {
        if ((sportTypeId === sportTypeIds.PICKLEBALL) || (sportTypeId === sportTypeIds.TENNIS && isDivisionDoubleOrDingles)) {
            return acceptanceCriteriaList.filter(criteria => criteria.value !== acceptanceValues.LIST);
        }

        return acceptanceCriteriaList;
    };

    // how many players can be accepted directly into the draw (before adding them to the waitlist in case of WAITLIST)
    const validateMaxPlayers = (acceptanceType, sportTypeId) => {
        if (acceptanceType === acceptanceValues.WAIT_LIST) {
            if (isVerifiedEvent && sportTypeId === sportTypeIds.TENNIS) {
                return createReactFormValidator([range(getMinValue(division?.acceptance?.type, sportTypeId), 256, [1, 2, 3]), positiveInteger]);
            }
            return createReactFormValidator([range(getMinValue(division?.acceptance?.type, sportTypeId), 256), positiveInteger]);
        }
        if (acceptanceType === acceptanceValues.CAPPED_REGISTRATION) {
            return createReactFormValidator([range(getMinValue(division?.acceptance?.type, sportTypeId), 256), positiveInteger, required]);
        }
        return createReactFormValidator([range(getMinValue(division?.acceptance?.type, sportTypeId), 256), positiveInteger]);
    };

    const getMinValue = (acceptanceType, sportTypeId) => {
        if (acceptanceType === acceptanceValues.WAIT_LIST) {
            return defaultAcceptanceMinValue;
        }
        if (acceptanceType === acceptanceValues.CAPPED_REGISTRATION) {
            if (sportTypeId === sportTypeIds.PICKLEBALL) {
                return defaultAcceptanceMinValue;
            }
            if (sportTypeId === sportTypeIds.TENNIS) {
                if (isVerifiedEvent) {
                    return verifiedAcceptanceMinValue;
                }
                return nonVerifiedAcceptanceMinValue;
            }
        }
    };

    const showAllowTeamOnWaitlist = (division, isDivisionDoubleOrDingles) => {
        return division?.acceptance?.type === acceptanceValues.WAIT_LIST && isDivisionDoubleOrDingles && parseInt(division?.teamPaymentTypeId) === teamPaymentTypeIds.PAY_SEPARATELY;
    };
    return (
        <>
            <div className={`form-block-divider`}>
                <div className="title">
                    <div className="text-small text-uppercase text-medium" data-testid="divisionacceptancelist.div-157">Acceptance List</div>
                    <small style={{ textTransform: 'unset' }}>Configure acceptance criteria</small>
                </div>
            </div>
            <div className={`form-block-content`}>
                <div
                    className="form-group form-group-xl"
                    data-test="acceptanceTypeSelect"
                >
                    <DefaultSelect
                        id={`divisions${divisionIndex}acceptanceCriteria`}
                        label="How would you like to accept your registrants"
                        emptyOption="Select an acceptance criteria"
                        field={[field, divisionIndex, 'acceptance', 'type']}
                        options={getDivisionAcceptanceList(parseInt(sportTypeId), acceptanceCriteriaList, isDivisionDoubleOrDingles)}
                        onChange={val => onAcceptanceTypeChange(val)}
                        validate={required}
                    />
                </div>
                {(division?.acceptance?.type === acceptanceValues.WAIT_LIST || division?.acceptance?.type === acceptanceValues.CAPPED_REGISTRATION) && (
                    <>
                        <Input
                            id={`divisions${divisionIndex}players`}
                            label={getDivisionSelectLabel(isDivisionSingles, division)}
                            field={[field, divisionIndex, 'maxPlayers']}
                            placeholder={division?.acceptance?.type === acceptanceValues.WAIT_LIST ? 'No Maximum' : null}
                            type="number"
                            min={getMinValue(division?.acceptance?.type, parseInt(sportTypeId))}
                            max={256}
                            validate={validateMaxPlayers(division?.acceptance?.type, parseInt(sportTypeId))}
                            data-testid="event-edit.divisionsitemplayers-input"
                        />
                        {showAllowTeamOnWaitlist(division, isDivisionDoubleOrDingles) && (
                            <ToggleSwitch
                                id={`divisions${divisionIndex}allowTeamOnWaitlist`}
                                label="Place teams on the waitlist until both partners have completed registration?"
                                field={[field, divisionIndex, 'allowTeamOnWaitlist']}
                                    data-testid="event-edit.divisionsitemallow-team-on-wai-toggle"
                                hoverText="When enabled, partners are placed directly on the waitlist until both partners have completed registration and/or payment. After both partners complete these steps, they move to the main draw, provided the maximum team limit hasn't been reached."
                                reverse={false}
                            />
                        )}
                    </>
                )}
                {division.acceptance && division.acceptance.type === acceptanceValues.LIST && (
                    <>
                        <Buckets
                            field={field}
                            buckets={division.acceptance.list.buckets}
                            divisionIndex={divisionIndex}
                            acceptanceListRoundTypes={acceptanceListRoundTypes}
                            isDivisionSingles={isDivisionSingles}
                        />
                        <div
                            className="form-group form-group-xl"
                            data-test="acceptanceRankingSelect"
                        >
                            <AcceptanceRankTypeSelect
                                field={field}
                                divisionIndex={divisionIndex}
                                isDivisionGenderMaleOrFemale={isDivisionGenderMaleOrFemale}
                                isDivisionGenderFemale={isDivisionGenderFemale}
                                isPTTEvent={isPTTEvent}
                                onChange={onAcceptanceRankTypeChange}
                            />
                        </div>
                        {showUtrRatingInputs && (
                            <Select
                                data-test="utrRatingTypeSelect"
                                label="Rating Type"
                                field={[field, divisionIndex, 'acceptance', 'list', 'rating']}
                                options={acceptanceListRatingTypes}
                                validate={required}
                            />
                        )}
                        {showWtaRankingInputs && (
                            <RankingBuckets
                                field={field}
                                buckets={division.acceptance.list.buckets}
                                division={division}
                                divisionIndex={divisionIndex}
                                acceptanceListRoundTypes={acceptanceListRoundTypes}
                            />
                        )}
                    </>
                )}
            </div>
        </>
    );
};

DivisionAcceptanceList.propTypes = {
    field: PropTypes.string.isRequired,
    formApi: PropTypes.objectOf(any).isRequired,
    division: PropTypes.objectOf(any).isRequired,
    divisionIndex: PropTypes.number,
    sportTypeId: PropTypes.number,
};

DivisionAcceptanceList.defaultProps = {
    divisionIndex: 0,
    sportTypeId: sportTypeIds.TENNIS,
};

export default DivisionAcceptanceList;
